import React, { useState, useEffect } from "react";
import { Container, Button } from "react-bootstrap"; 
import { Link } from "react-router-dom"; 
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"; 
import { faCog } from "@fortawesome/free-solid-svg-icons"; 
import Notice from "./Notice";
import EmailLink from "./EmailLink";
import PropTypes from "prop-types";
import { accessError, blockedKitError, userNotFound } from "../../utils/constants";

/**
 * ErrorDisplay component
 *
 * This component displays error messages based on the provided 'error' and 'consent' props.
 * It handles different scenarios like showing a general error message, displaying a maintenance mode message,
 * or providing options to contact support.
 * @returns {JSX.Element} The rendered component displaying the appropriate error message and actions.
 */

export function ErrorDisplay({ error, consent, alreadyActivated, testType, secondaryBarcode }) {
  const [redirect, setRedirect] = useState("");
  const [displayCustomercare, setDisplayCustomercare] = useState(true);
  useEffect(() => {
    if ((testType?.startsWith("dna") || testType?.startsWith("boots-dna-")) && !secondaryBarcode) {
      setRedirect("/tests/dna/activate");
    }
    else if (secondaryBarcode === "urine") {
      setRedirect("/tests/sh/activate");
    }
    else {
      setRedirect("/tests/bloods/activate");
    }
  }, [testType, secondaryBarcode]);

  useEffect(() => {

    // Check if error is a string or an object
    const errorMessage = typeof error === 'string' ? error : error.message;

    // Check for specific error messages
    if (errorMessage === accessError ||
      errorMessage === userNotFound ||
      errorMessage=== blockedKitError) {
      setDisplayCustomercare(false);
    } else {
      setDisplayCustomercare(true);
    }
  }, [error]);

  if (alreadyActivated) {
    return (
      <Container className="my-5">
        <h1>Error</h1>
        <p>{error}</p>
        <p>Barcode is already activated, please check the status of your barcode and try again.</p>
        <Button
          as={Link}
          to={redirect}
          variant="primary"
          className="mr-2"
        >
          Previous step
        </Button>
        <Button
          as="a"
          href="https://myhealthchecked.com/contact"
          target="_blank"
          rel="noopener noreferrer"
          variant="outline-primary"
        >
          Contact us
        </Button>
      </Container>
    );
  }
  if (consent) {
    return (
      <Container className="my-5">
        <h1>Error</h1>
        <p>{error}</p>
        <p>If you need assistance, please contact support.</p>
        <div className="d-flex flex-wrap gap-3">
          <Button
            as={Link}
            to={redirect}
            variant="primary"
          >
            Previous step
          </Button>
          <Button
            as="a"
            href="https://myhealthchecked.com/contact"
            target="_blank"
            rel="noopener noreferrer"
            variant="outline-primary"
          >
            Contact us
          </Button>
        </div>
      </Container>
    );
  }

  // Check if the error is related to maintenance (503 status code)
  if (error.details?.status === 503) {
    return (
      <div className="maintenance">
        <Notice title="Maintenance Mode">
          <FontAwesomeIcon icon={faCog} size="6x" className="loading" />
          <p>
            This system is currently in maintenance mode, please check back in a
            few minutes.
          </p>
          <p>
            If you have any questions, please contact <EmailLink />.
          </p>
        </Notice>
      </div>
    );
  }

  // Default case when consent is false, render the fallback error message
  else {
    return (
      <Notice title="Snap!">
        {displayCustomercare && (
          <>
            <p>
              Looks like something went wrong, we have been notified about this
              error and will work on a fix as soon as time and coffee supplies
              allow.
            </p>
            <p>
              In the meantime, if you continue to experience errors, please contact{" "}
              <EmailLink />.
            </p>
            <p>
              Here's what we know so far:
            </p>
          </>
        )}
        <p>
          <span className="text-dark">
            {(typeof error === "string" ? error : typeof error?.message === "string" ? error.message : null) ||
              "An unexpected error occurred."}
          </span>
        </p>
      </Notice>
    );
  }
}

ErrorDisplay.propTypes = {
  error: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.shape({
      message: PropTypes.string,
      details: PropTypes.shape({
        status: PropTypes.number,
      }),
    }),
  ]).isRequired,
  consent: PropTypes.bool.isRequired,
  alreadyActivated: PropTypes.bool.isRequired,
  testType: PropTypes.string,
  secondaryBarcode: PropTypes.string,
};